import { useState } from 'react';
import { BookOpen, Newspaper, ExternalLink } from 'lucide-react';
import {
  LIBRARY_BOOKS,
  LIBRARY_EDITIONS,
  type LibraryBook,
  type LibraryEdition,
} from '../data/library';

// Book cover: tries the S3 URL, falls back to a colored placeholder
function BookCover({ src, title, index }: { src: string; title: string; index: number }) {
  const [failed, setFailed] = useState(false);
  const GRADIENTS = [
    'from-xd-info/10 to-xd-info/20',
    'from-xd-success/10 to-xd-success/20',
    'from-xd-action-end/10 to-xd-xp/20',
    'from-xd-badge/10 to-xd-badge/20',
    'from-xd-error/10 to-xd-error/20',
    'from-xd-info/10 to-xd-badge/20',
  ];
  const grad = GRADIENTS[index % GRADIENTS.length];

  if (failed) {
    return (
      <div
        role="img"
        aria-label={title}
        className={`w-full h-full bg-gradient-to-br ${grad} flex items-center justify-center text-[var(--xd-fg-4)]`}
      >
        <BookOpen className="h-10 w-10" aria-hidden />
      </div>
    );
  }

  return (
    <img src={src} alt="" className="w-full h-full object-cover" onError={() => setFailed(true)} />
  );
}

function EditionCover({ src, title, index }: { src: string; title: string; index: number }) {
  const [failed, setFailed] = useState(false);
  const GRADIENTS = [
    'from-xd-error/10 to-xd-error/20',
    'from-surface-2 to-surface-2',
    'from-xd-xp/10 to-xd-xp/20',
  ];
  const grad = GRADIENTS[index % GRADIENTS.length];

  if (failed) {
    return (
      <div
        role="img"
        aria-label={title}
        className={`w-full h-full bg-gradient-to-br ${grad} flex items-center justify-center text-[var(--xd-fg-4)]`}
      >
        <Newspaper className="h-8 w-8" aria-hidden />
      </div>
    );
  }

  return (
    <img src={src} alt="" className="w-full h-full object-cover" onError={() => setFailed(true)} />
  );
}

function BookCard({ book, index }: { book: LibraryBook; index: number }) {
  return (
    <a
      href={book.url}
      target="_blank"
      rel="noopener noreferrer"
      className="card-hover group flex flex-col bg-surface border border-border rounded-xl overflow-hidden"
    >
      {/* Cover */}
      <div className="aspect-[3/4] overflow-hidden">
        <BookCover src={book.coverUrl} title={book.title} index={index} />
      </div>

      {/* Info */}
      <div className="p-4 flex-1 flex flex-col gap-1">
        <h3 className="font-semibold text-text text-sm leading-snug line-clamp-2">{book.title}</h3>
        <p className="text-xs text-text-subtle">{book.author}</p>
        <p className="text-xs text-text-subtle mt-auto pt-2">{book.year}</p>
      </div>

      {/* Hover link indicator */}
      <div className="px-4 pb-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="flex items-center gap-1 text-xs text-xd-action font-medium">
          <ExternalLink className="h-3 w-3" />
          Abrir en Library
        </span>
      </div>
    </a>
  );
}

function EditionCard({ edition, index }: { edition: LibraryEdition; index: number }) {
  return (
    <a
      href={edition.url}
      target="_blank"
      rel="noopener noreferrer"
      className="card-hover group flex bg-surface border border-border rounded-xl overflow-hidden"
    >
      {/* Cover — landscape thumbnail */}
      <div className="w-24 flex-shrink-0 overflow-hidden">
        <EditionCover src={edition.coverUrl} title={edition.displayName} index={index} />
      </div>

      {/* Info */}
      <div className="flex-1 p-4 flex flex-col justify-between">
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
            {edition.year}
          </span>
          <h3 className="font-semibold text-text text-sm mt-0.5 leading-snug">
            {edition.displayName}
          </h3>
        </div>
        <span className="flex items-center gap-1 text-xs text-xd-action font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-2">
          <ExternalLink className="h-3 w-3" />
          Abrir en Library
        </span>
      </div>
    </a>
  );
}

export default function LibraryBooks() {
  return (
    <div className="page-enter max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-text">Biblioteca</h1>
          <p className="text-sm text-text-muted mt-1">
            Libros y revistas disponibles en xdface Library
          </p>
        </div>
        <a
          href="https://library.xdface.net"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-text-muted hover:border-xd-action hover:text-xd-action transition-colors"
        >
          <ExternalLink className="h-4 w-4" />
          Ir a Library
        </a>
      </div>

      {/* Books */}
      <section className="mb-10">
        <h2 className="text-base font-semibold text-text mb-4 flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-xd-action" />
          Libros
          <span className="text-xs font-normal text-text-subtle">{LIBRARY_BOOKS.length}</span>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {LIBRARY_BOOKS.map((book, i) => (
            <BookCard key={book.slug} book={book} index={i} />
          ))}
        </div>
      </section>

      {/* HBR Editions */}
      <section>
        <h2 className="text-base font-semibold text-text mb-4 flex items-center gap-2">
          <Newspaper className="h-4 w-4 text-xd-action" />
          Revistas HBR
          <span className="text-xs font-normal text-text-subtle">{LIBRARY_EDITIONS.length}</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LIBRARY_EDITIONS.map((edition, i) => (
            <EditionCard key={edition.slug} edition={edition} index={i} />
          ))}
        </div>
      </section>

      {/* Footer note */}
      <p className="text-xs text-text-subtle mt-10 text-center">
        El contenido se abre en{' '}
        <a
          href="https://library.xdface.net"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xd-action hover:underline"
        >
          library.xdface.net
        </a>{' '}
        — requiere sesión activa en Library.
      </p>
    </div>
  );
}
